/**
 * Fetch wrapper that adds x-trace-id header for request correlation and
 * automatically logs request start / completion / failure with duration_ms,
 * plus structured request-body and response-body attributes (language,
 * target_role, interview_company, status, …) when the content is JSON. Pass
 * a logger to enable timed logging; omit it for a silent pass-through.
 */
import { generateTraceId } from "./trace"
import { attributesFromFetchBody, flattenForLog, isStreamingContentType } from "./log-attributes"
import {
  classifyFetchFailure,
  isExpectedHttpStatus,
  resolveFetchFailureLevel,
} from "./fetch-failure"
import {
  generateBrowserSpanId,
  getBrowserSessionId,
} from "./browser-observability-identity"

type LogFn = (message: string, data?: Record<string, unknown> | Error) => void

interface LoggerLike {
  info: LogFn
  warn: LogFn
  error: LogFn
}

export type FetchWithTraceInit = RequestInit & {
  traceId?: string
  logger?: LoggerLike
  logContext?: Record<string, unknown>
}

function now(): number {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now()
}

function describeUrl(input: RequestInfo | URL): { url: string; path: string } {
  const raw =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.toString()
        : (input as Request).url
  try {
    const parsed = new URL(raw)
    return { url: raw, path: parsed.pathname }
  } catch {
    return { url: raw, path: raw }
  }
}

export async function fetchWithTrace(
  input: RequestInfo | URL,
  init?: FetchWithTraceInit
): Promise<{ response: Response; traceId: string }> {
  const traceId = init?.traceId ?? generateTraceId()
  const { traceId: _t, logger, logContext, ...restInit } = init ?? {}
  const headers = new Headers(restInit?.headers)
  const spanId = generateBrowserSpanId()
  headers.set("x-trace-id", traceId)
  headers.set("x-session-id", getBrowserSessionId())
  headers.set("traceparent", `00-${traceId}-${spanId}-01`)
  const method = (restInit.method || "GET").toUpperCase()
  const { url, path } = describeUrl(input)
  const requestBodyAttrs = attributesFromFetchBody(restInit.body, "req")
  // Datadog reserves `trace_id` for APM correlation and hides it from
  // the body when the value isn't APM-formatted. Emit as
  // `correlation_id` (canonical) plus `request_id` (alias) instead.
  // The outgoing HTTP header keeps the `x-trace-id` name for backend
  // backward compat.
  const baseCtx = {
    trace_id: traceId,
    span_id: spanId,
    correlation_id: traceId,
    request_id: traceId,
    request_method: method,
    request_url: url,
    request_path: path,
    ...(logContext ?? {}),
  }

  const startedAt = now()
  if (logger) {
    logger.info("http_request_start", {
      ...baseCtx,
      action: "http_request_start",
      ...requestBodyAttrs,
    })
  }

  try {
    const response = await fetch(input, { ...restInit, headers })
    const durationMs = Math.round(now() - startedAt)
    if (logger) {
      const respContentType = response.headers.get("content-type") || ""
      const streaming = isStreamingContentType(respContentType)
      const baseComplete = {
        ...baseCtx,
        action: "http_request_complete",
        status: response.status,
        ok: response.ok,
        duration_ms: durationMs,
        response_content_type: respContentType,
        response_streaming: streaming,
        ...requestBodyAttrs,
      }
      // Deliberate protocol statuses (409 /api/user/me rotation-grace) are
      // info, not warn — see isExpectedHttpStatus in lib/fetch-failure.ts.
      const level = response.ok || isExpectedHttpStatus(response.status, path) ? "info" : "warn"
      if (!streaming && respContentType.includes("application/json")) {
        response
          .clone()
          .json()
          .then((body) => {
            logger[response.ok || isExpectedHttpStatus(response.status, path, body) ? "info" : "warn"]("http_request_complete", {
              ...baseComplete,
              ...flattenForLog(body, "resp"),
            })
          })
          .catch(() => {
            logger[level]("http_request_complete", baseComplete)
          })
      } else {
        logger[level]("http_request_complete", baseComplete)
      }
    }
    return { response, traceId }
  } catch (err) {
    const durationMs = Math.round(now() - startedAt)
    if (logger) {
      // Classify so the log says *why* it failed: an aborted request (user
      // navigated away / superseded), an offline/network blip, or a genuine
      // unexpected error. Only the last stays at error severity. Transient
      // kinds are warnings — unless the environment already explains them
      // (offline, or the page is hidden mid-teardown), which demotes to info;
      // see resolveFetchFailureLevel. Filter future occurrences by
      // @failure_kind and the online / page_visibility context below.
      const classified = classifyFetchFailure(err)
      const online = typeof navigator !== "undefined" ? navigator.onLine : undefined
      const pageVisibility =
        typeof document !== "undefined" ? document.visibilityState : undefined
      const level = resolveFetchFailureLevel(classified, { online, pageVisibility })
      logger[level]("http_request_failed", {
        ...baseCtx,
        action: "http_request_failed",
        failure_kind: classified.kind,
        duration_ms: durationMs,
        error_message: err instanceof Error ? err.message : String(err),
        error_name: err instanceof Error ? err.name : "UnknownError",
        online,
        page_visibility: pageVisibility,
        ...requestBodyAttrs,
      })
    }
    throw err
  }
}
